/**
 * Box Primitive - FluentUI Design Kit
 */

import React from 'react';
import DOMPurify from 'dompurify';
import type { BoxProps } from './Box.types';
import { useBoxHelpers } from './helpers/boxHelper';

/**
 * Box - A utility component for layout and styling with Design Kit tokens
 *
 * Makes it easy to use Design Kit tokens without remembering exact token names.
 * Supports refs for DOM access (scrolling, focus management, etc.)
 *
 * @example
 * <Box p="m" bg="surface1" borderRadius="medium">
 *   <Box color="text1">Content with medium padding, surface background</Box>
 * </Box>
 *
 * @example
 * const scrollRef = useRef<HTMLDivElement>(null);
 * <Box ref={scrollRef} style={{ overflowY: 'auto' }}>
 *   Scrollable content
 * </Box>
 */
export const Box = React.forwardRef<HTMLElement, BoxProps>(
  (
    {
      children,
      as = 'div',
      // Spacing props
      p,
      px,
      py,
      pt,
      pr,
      pb,
      pl,
      m,
      mx,
      my,
      mt,
      mr,
      mb,
      ml,
      // Color props
      bg,
      color,
      // Border radius
      borderRadius,
      // Layout props
      display,
      flexDirection,
      alignItems,
      justifyContent,
      gap,
      // Size props
      width,
      height,
      maxWidth,
      maxHeight,
      // Additional props
      style = {},
      className,
      htmlContent,
      onClick,
      onMouseEnter,
      onMouseLeave,
      ...rest
    },
    ref,
  ) => {
    const { buildBoxStyles } = useBoxHelpers();

    // Build styles using Design Kit tokens
    const boxStyles = buildBoxStyles({
      p,
      px,
      py,
      pt,
      pr,
      pb,
      pl,
      m,
      mx,
      my,
      mt,
      mr,
      mb,
      ml,
      bg,
      color,
      borderRadius,
      display,
      flexDirection,
      alignItems,
      justifyContent,
      gap,
      width,
      height,
      maxWidth,
      maxHeight,
      style,
    });

    // If htmlContent is provided, sanitize and render as HTML
    const content = htmlContent
      ? React.createElement('div', {
        dangerouslySetInnerHTML: { __html: DOMPurify.sanitize(htmlContent) },
      })
      : children;

    return React.createElement(
      as,
      {
        ref,
        className,
        style: boxStyles,
        onClick,
        onMouseEnter,
        onMouseLeave,
        ...rest,
      },
      content,
    );
  },
);

// Add display name for better debugging
Box.displayName = 'Box';
